import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Copy, Trash2, Printer, Send, Plus, Loader2, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { gbp, INVOICE_STATUS_COLORS, statusLabel } from "@/lib/invoice";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pay, setPay] = useState({ amount: "", method: "cash", date: "", taken_by: "", note: "" });
  const [showPay, setShowPay] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: inv, isLoading } = useQuery({
    queryKey: ["invoice", id],
    queryFn: async () => (await api.get(`/admin/invoices/${id}`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["invoice", id] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
    qc.invalidateQueries({ queryKey: ["invoices-summary"] });
  };

  if (isLoading || !inv) return <p className="text-slate-500">Loading…</p>;

  const setStatus = async (status) => {
    await api.patch(`/admin/invoices/${id}/status`, { status });
    refresh();
    toast.success(`Marked as ${statusLabel(status)}`);
  };

  const recordPayment = async () => {
    const amt = Number(pay.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/invoices/${id}/payments`, { ...pay, amount: amt });
      setPay({ amount: "", method: "cash", date: "", taken_by: "", note: "" });
      setShowPay(false);
      refresh();
      toast.success("Payment recorded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deletePayment = async (pid) => {
    await api.delete(`/admin/invoices/${id}/payments/${pid}`);
    refresh();
    toast.success("Payment removed");
  };

  const duplicate = async () => {
    const { data } = await api.post(`/admin/invoices/${id}/duplicate`);
    qc.invalidateQueries({ queryKey: ["invoices"] });
    qc.invalidateQueries({ queryKey: ["invoices-summary"] });
    toast.success(`Duplicated as ${data.invoice_number}`);
    navigate(`/admin/invoices/${data.id}/edit`);
  };

  const remove = async () => {
    if (!window.confirm("Delete this invoice permanently?")) return;
    await api.delete(`/admin/invoices/${id}`);
    qc.invalidateQueries({ queryKey: ["invoices"] });
    qc.invalidateQueries({ queryKey: ["invoices-summary"] });
    toast.success("Invoice deleted");
    navigate("/admin/invoices");
  };

  const fillBalance = () => setPay((p) => ({ ...p, amount: inv.balance_due.toFixed(2) }));
  const inputCls = "w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100";

  return (
    <div data-testid="invoice-detail-page">
      <Link to="/admin/invoices" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-semibold"><ArrowLeft className="h-4 w-4" /> Back to invoices</Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display font-black text-slate-900 text-2xl sm:text-3xl font-mono">{inv.invoice_number}</h1>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${INVOICE_STATUS_COLORS[inv.status]}`} data-testid="invoice-status-badge">{statusLabel(inv.status)}</span>
          </div>
          <p className="text-slate-400 text-sm mt-1">Issued {inv.issue_date || "—"} · Due {inv.due_date || "—"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/admin/invoices/${id}/print`} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-green-700" data-testid="invoice-print"><Printer className="h-4 w-4" /> PDF</Link>
          <Link to={`/admin/invoices/${id}/edit`} className="h-10 w-10 rounded-xl bg-slate-100 grid place-items-center hover:bg-slate-200" data-testid="invoice-edit"><Pencil className="h-4.5 w-4.5 text-slate-600" /></Link>
          <button onClick={duplicate} className="h-10 w-10 rounded-xl bg-slate-100 grid place-items-center hover:bg-slate-200" data-testid="invoice-duplicate"><Copy className="h-4.5 w-4.5 text-slate-600" /></button>
          <button onClick={remove} className="h-10 w-10 rounded-xl bg-red-50 grid place-items-center hover:bg-red-100" data-testid="invoice-delete"><Trash2 className="h-4.5 w-4.5 text-red-600" /></button>
        </div>
      </div>

      {inv.status === "draft" && (
        <button onClick={() => setStatus("sent")} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700" data-testid="invoice-mark-sent"><Send className="h-4 w-4" /> Mark as sent</button>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {/* Left: customer + items */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-700 mb-2">Bill to</div>
            <div className="font-bold text-slate-900 text-lg">{inv.customer_name}</div>
            <div className="mt-2 space-y-1 text-sm text-slate-600">
              {inv.customer_phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-green-600" /> {inv.customer_phone}</div>}
              {inv.customer_email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-green-600" /> {inv.customer_email}</div>}
              {(inv.customer_address || inv.customer_postcode) && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-green-600" /> {[inv.customer_address, inv.customer_postcode].filter(Boolean).join(", ")}</div>}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="pb-2 font-semibold">Description</th>
                  <th className="pb-2 font-semibold text-right">Qty</th>
                  <th className="pb-2 font-semibold text-right">Unit</th>
                  <th className="pb-2 font-semibold text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {inv.line_items.map((li, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-2.5 text-slate-800">{li.description || "—"}</td>
                    <td className="py-2.5 text-right text-slate-600">{li.quantity}</td>
                    <td className="py-2.5 text-right text-slate-600">{gbp(li.unit_price)}</td>
                    <td className="py-2.5 text-right font-semibold text-slate-800">{gbp(li.quantity * li.unit_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 space-y-1.5 max-w-xs ml-auto text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{gbp(inv.subtotal)}</span></div>
              {inv.vat_enabled && <div className="flex justify-between"><span className="text-slate-500">VAT ({inv.vat_rate}%)</span><span className="font-semibold">{gbp(inv.vat_amount)}</span></div>}
              <div className="flex justify-between border-t border-slate-100 pt-1.5"><span className="font-bold text-slate-900">Total</span><span className="font-display font-black text-slate-900">{gbp(inv.total)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Paid</span><span className="font-semibold text-emerald-600">{gbp(inv.amount_paid)}</span></div>
              <div className="flex justify-between"><span className="font-bold text-slate-900">Balance due</span><span className="font-display font-black text-amber-600">{gbp(inv.balance_due)}</span></div>
            </div>
            {inv.notes && <div className="mt-4 border-t border-slate-100 pt-3 text-sm text-slate-500"><span className="font-semibold text-slate-700">Notes: </span>{inv.notes}</div>}
          </div>
        </div>

        {/* Right: payments */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm h-fit">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-slate-900">Payments</h2>
            {inv.balance_due > 0 && <button onClick={() => { setShowPay(!showPay); fillBalance(); }} className="inline-flex items-center gap-1 text-sm font-bold text-green-700" data-testid="invoice-record-payment-toggle"><Plus className="h-4 w-4" /> Record</button>}
          </div>

          {showPay && (
            <div className="rounded-xl bg-slate-50 p-4 space-y-3 mb-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Amount (£)</label>
                <input type="number" step="0.01" className={inputCls} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Method</label>
                <select className={inputCls} value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} data-testid="payment-method">
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="card">Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Date</label><input type="date" className={inputCls} value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} data-testid="payment-date" /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Taken by</label><input className={inputCls} value={pay.taken_by} onChange={(e) => setPay({ ...pay, taken_by: e.target.value })} data-testid="payment-takenby" /></div>
              </div>
              <button onClick={recordPayment} disabled={saving} className="w-full rounded-xl bg-green-600 py-2.5 font-bold text-white hover:bg-green-700 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="payment-save">
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save payment"}
              </button>
            </div>
          )}

          {(inv.payments || []).length === 0 ? (
            <p className="text-sm text-slate-400">No payments recorded.</p>
          ) : (
            <ul className="space-y-2">
              {inv.payments.map((p) => (
                <li key={p.id} className="flex items-start justify-between gap-2 rounded-xl bg-slate-50 px-3 py-2.5" data-testid={`payment-${p.id}`}>
                  <div>
                    <div className="font-bold text-slate-900">{gbp(p.amount)} <span className="text-xs font-medium text-slate-500 capitalize">· {p.method.replace("_", " ")}</span></div>
                    <div className="text-xs text-slate-400">{p.date}{p.taken_by ? ` · ${p.taken_by}` : ""}</div>
                  </div>
                  <button onClick={() => deletePayment(p.id)} className="h-7 w-7 rounded-lg bg-red-50 grid place-items-center hover:bg-red-100 shrink-0" data-testid={`payment-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
